import React, { useState } from 'react';
import Radio from '../../../../Radio/Radio';
import StepperInput from '../../../../StepperInput/StepperInput';
import ActionButtons from './ActionButtons';
import { useQueryBuilder } from '../context/QueryBuilderContext';
import { FILTER_OPERATORS } from '../../../utils/constants';

interface NumberDropdownProps {
  field: string;
  closeDropdown: () => void;
  
}

enum Mode {
  SINGLE = 'single',
  RANGE = 'range',
}

const NumberDropdown: React.FC<NumberDropdownProps> = ({ field, closeDropdown }) => {
  const { setCondition, getConditionValue, removeCondition } = useQueryBuilder();
  const existing = getConditionValue(field);
  const rangeObj = existing?.[FILTER_OPERATORS.RANGE];
  const isRange = rangeObj && (FILTER_OPERATORS.GREATER_THAN_OR_EQUAL in rangeObj || FILTER_OPERATORS.LESS_THAN_OR_EQUAL in rangeObj);
  const isSingle = existing?.[FILTER_OPERATORS.EQUALS] !== undefined;


  const initialMode: Mode = isRange ? Mode.RANGE : isSingle ? Mode.SINGLE : Mode.SINGLE;

  const initialSingleValue = initialMode === Mode.SINGLE ? existing?.[FILTER_OPERATORS.EQUALS] ?? null : null;
  const initialMin = initialMode === Mode.RANGE ? rangeObj?.[FILTER_OPERATORS.GREATER_THAN_OR_EQUAL] ?? null : null;
  const initialMax = initialMode === Mode.RANGE ? rangeObj?.[FILTER_OPERATORS.LESS_THAN_OR_EQUAL] ?? null : null;
  const [mode, setMode] = useState<Mode.SINGLE | Mode.RANGE>(initialMode);
  const [singleValue, setSingleValue] = useState<number | null>(initialSingleValue);
  const [minDisplay, setMinDisplay] = useState<number | null>(initialMin);
  const [maxDisplay, setMaxDisplay] = useState<number | null>(initialMax);


  const handleModeChange = (value: Mode.SINGLE | Mode.RANGE) => {
    setMode(value);
  };

  const handleSingleChange = (val: number | null) => setSingleValue(val);
  const handleMinChange = (value: number | null) => {
    setMinDisplay(value);
  };

  const handleMaxChange = (value: number | null) => {
    setMaxDisplay(value);
  };

  const clearAll = () => {
    setSingleValue(null);
    setMinDisplay(null);
    setMaxDisplay(null);
  };

  const applyFilter = () => {
    removeCondition(field);

    if (mode === Mode.SINGLE) {
      if (singleValue !== null) {
        setCondition(field, FILTER_OPERATORS.EQUALS, singleValue);
      }
    } else if (mode === Mode.RANGE) {
      const hasMin = minDisplay !== null;
      const hasMax = maxDisplay !== null;

      if (hasMin || hasMax) {
        setCondition(field, FILTER_OPERATORS.RANGE, {
          ...(hasMin ? { $gte: minDisplay } : {}),
          ...(hasMax ? { $lte: maxDisplay } : {}),
        });
      }
    }

    closeDropdown();
  };

  return (
    <div className="NumberDropdown">
      <b>{'Choose Input Type'}</b>
      <div className="NumberDropdown__radio-selection">
        <Radio
          label={'Single'}
          name="number-dropdown"
          value="single"
          onChange={() => handleModeChange(Mode.SINGLE)}
          checked={mode === Mode.SINGLE}
          version="v2"
        />
        <Radio
          label={'Range'}
          name="number-dropdown"
          value="range"
          onChange={() => handleModeChange(Mode.RANGE)}
          checked={mode === Mode.RANGE}
          version="v2"
        />
      </div>

      {mode === Mode.SINGLE ? (
        <StepperInput
          min={0}
          max={999999999}
          step={1}
          value={singleValue}
          onChange={handleSingleChange}
          placeholder={'Value'}
          inputWidth="3.5rem"
        />
      ) : (
        <div className="NumberDropdown__range-inputs" style={{ display: 'flex', gap: '0.5rem' }}>
          <StepperInput
            min={0}
            max={999999999}
            step={1}
            value={minDisplay}
            onChange={handleMinChange}
            placeholder={'Min'}
            inputWidth="3.5rem"
          />
          <StepperInput
            min={0}
            max={999999999}
            step={1}
            value={maxDisplay}
            onChange={handleMaxChange}
            placeholder={'Max'}
            inputWidth="3.5rem"
          />
        </div>
      )}

      <ActionButtons
        closeDropdown={closeDropdown}
        clearAll={clearAll}
        applyFilter={applyFilter}
      />
    </div>
  );
};

export default NumberDropdown;
